// IMPORT PACKAGE REFERENCES
import { Box, Button, ButtonGroup, Card, CardContent, CardHeader, Checkbox, Chip, Divider, Grid, IconButton, ListItem, ListItemSecondaryAction, makeStyles } from "@material-ui/core";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import PropTypes from 'prop-types';
import { default as React, Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { AutoSizer } from "react-virtualized";
import { FixedSizeList } from "react-window";
import { getIcon } from "../../icon/icon";
import { Loading } from "../loading/Loading";
import { PagePlaceholder } from "../page-placeholder/PagePlaceholder";
import ShrinkingButtonGroup from "../shrinking-button-group/ShrinkingButtonGroup";
import Toolbar from "../toolbar/Toolbar";
import { CustomListItemText } from "./CustomListItemText";
import { CustomSecondaryListItemText } from "./CustomSecondaryListItemText";

const useStyles = makeStyles(theme => ({
  smallScreen: {
    display: "none"
  },
  cardContent: {
    height: "100%",
    overflow: "auto",
    maxHeight: "400px"
  },
  "@media only screen and (max-width: 1950px)": {
    largeScreen: {
      display: "none"
    },

    smallScreen: {
      display: "inherit",
      fontSize: 16
    }
  },
  placeholder: {
    opacity: "0.5"
  },
  icon: {
    height: "22px"
  },
  cardHeaderAction: {
    marginTop: 0,
    marginRight: 0
  },
  chip: {
    marginRight: "8px"
  },
  listItemText: {
    color: "#4C73FF",
    fontWeight: 400,
    cursor: "pointer",
    '&:hover': {
      opacity: 0.75
    }
  },
  list: {
    background:
      "linear-gradient(white 30%, rgba(255, 255, 255, 0)), " +
      "linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%, " +
      "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0) 100%)",
    backgroundRepeat: "no-repeat",
    backgroundColor: "white",
    backgroundSize: "100% 40px, 100% 40px, 100% 14px, 100% 14px",
    backgroundAttachment: "local, local, scroll, scroll",
    borderTop: "solid 0.5px rgba(0,0,0,0.2)"
  }
}))

function ListableCard(props) {
  // Import styles from our useStyles constant.
  const classes = useStyles();
  const [t, i18n] = useTranslation()
  // Define state
  const [selectedItems, setSelectedItems] = useState({});
  const [toolbarShowing, setToolbarShowing] = useState(false);

  // Set our disabled flag
  const disabled = !!props.disabled;

  // Define actionButtons
  const actionButtons = (props.actionButton ?
    props.actionButton.map(action => {
      return (
        {
          label: action.props.children,
          action: () => action.props.onClick()
        }
      )
    }) :
    [])


  function toggleToolbar() {
    setToolbarShowing(!disabled);
    setSelectedItems({});
  }

  function handleCheck(id) {
    const localSelectedItems = Object.assign({}, selectedItems);
    if (!selectedItems.hasOwnProperty(id)) {
      localSelectedItems[id] = true;
    } else {
      delete localSelectedItems[id];
    }

    setSelectedItems(localSelectedItems);
  }

  function multiSelect(shouldDeselect) {
    const localSelectedItems = {};

    if (shouldDeselect) {
      setSelectedItems({ localSelectedItems });
      return;
    }

    props.data.forEach(data => {
      let id = "";

      if (data !== null && data !== undefined) {
        id = data.id === null || data.id === undefined ? "" : data.id;
      }

      localSelectedItems[id] = true;
    });

    setSelectedItems(localSelectedItems);
  }

  function getDataList() {

    if (props.isLoading) {
      return <Loading />;
    }

    if (props.data.length === 0) {
      return (
        <div className={classes.placeholder}>{props.emptyPlaceholder}</div>
      );
    }

    const allSelected =
      Object.keys(selectedItems).length === props.data.length;

    const Row = ({ index, style }) => {
      const dataItem = props.data[index]
      if (dataItem != null) {
        let name = "";
        let id = "";

        name = dataItem.Name === null || dataItem.Name === undefined ? "" : dataItem.Name;
        id = dataItem.id === null || dataItem.id === undefined ? "" : dataItem.id;

        const dividerStyle = {
          position: style.position,
          top: style.top + 75,
          left: style.left,
          width: style.width,
          height: "1px"
        }

        return (
          <Box>
            <ListItem
              key={dataItem.id}
              role={undefined}
              style={style}
              disableGutters={true}
            >
              {!disabled && (
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    onClick={() => handleCheck(id)}
                    checked={selectedItems.hasOwnProperty(id)}
                    inputProps={{ "aria-labelledby": dataItem.id }}
                  />
                </ListItemIcon>
              )}
              <ListItemText
                className={dataItem.notClickable ? null : props.onClick ? classes.listItemText : null}
                id={dataItem.id}
                onClick={dataItem.notClickable ? null : props.onClick ? () => props.onClick(id) : null}
                primary={name}
                secondary={id}
              />
              {dataItem.secondaryText ?
                <CustomSecondaryListItemText text={dataItem.secondaryText} /> :
                null}
              {!disabled && (
                <ListItemIcon>
                  <IconButton
                    onClick={() => {
                      props.handleDelete([dataItem.id])
                      setSelectedItems({});
                    }}
                    edge="end"
                    aria-labelledby={dataItem.id}
                  >
                    {getIcon("trash.svg", classes.icon)}
                  </IconButton>
                </ListItemIcon>)}
            </ListItem>
            <Divider key={dataItem.id + "__divider"} style={dividerStyle} />
          </Box>
        )
      }
    }

    const showToolbar = !disabled && (props.data.length > 0)
    const cellHeight = 75
    // Maximum height of list = cell height * number of items in list, max 4
    const maxHeight = cellHeight * Math.min(props.data.length || 0, 4)
    const toolBarHeight = 30
    return (
      <Box style={{ height: `${maxHeight}px` }}>
        {props.data.length === 0 ?
          <PagePlaceholder text={props.emptyPlaceholder} /> :
          <Fragment>
            {
              showToolbar && (
                <Toolbar
                  showDelete={Object.keys(selectedItems).length > 0}
                  onDelete={() => {
                    props.handleDelete(Object.keys(selectedItems));
                    setSelectedItems({});
                  }}
                  multiSelectLabel={allSelected ? t('common.deselect-all') : t('common.select-all')}
                  multiSelect={() => multiSelect(allSelected)}
                />
              )
            }
            <AutoSizer>
              {({ height, width }) => (
                <FixedSizeList
                  height={height - (showToolbar ? toolBarHeight : 0)}
                  itemCount={props.data.length}
                  itemSize={cellHeight}
                  width={width}
                  className={classes.list}
                >
                  {Row}
                </FixedSizeList>
              )}
            </AutoSizer>
          </Fragment>
        }
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardHeader
          className={classes.cardHeader}
          title={props.title}
          secondary={"test"}
          classes={{ action: classes.cardHeaderAction }}
          action={
            disabled ? null :
              props.actionButton.length < 2 ?
                <Box>
                  <Chip className={classes.chip} label={props.data.length} variant="outlined" />
                  <ButtonGroup>
                    {!props.isLoading && props.actionButton}
                    {(props.data.length > 0) &&
                      <Button onClick={() => toggleToolbar()} variant="outlined">{!disabled ? t('common.done') : t('common.edit')}</Button>
                    }
                  </ButtonGroup>
                </Box>
                :
                <Box className={classes.cardHeaderAction}>
                  <Box className={classes.largeScreen}>
                    <Chip className={classes.chip} label={props.data.length} variant="outlined" />
                    <ButtonGroup>
                      {!props.isLoading && props.actionButton}
                      {(props.data.length > 0) &&
                        <Button onClick={toggleToolbar} variant="outlined">{!disabled ? t('common.done') : t('common.edit')}</Button>
                      }
                    </ButtonGroup>
                  </Box>
                  <Box className={classes.smallScreen}>
                    <ShrinkingButtonGroup
                      secondary={actionButtons}
                      default={{
                        label: "...",
                        action: null
                      }}
                    />
                  </Box>
                </Box>
          }
        >
        </CardHeader>
        <CardContent className={classes.cardContent}>
          {getDataList()}
        </CardContent>
      </Card>
    </Box>
  )
}

ListableCard.propTypes = {
  data: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  emptyPlaceholder: PropTypes.node,
  handleDelete: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  actionButton: PropTypes.node,
  title: PropTypes.string
}

export default ListableCard;
